import {Injectable} from '@angular/core';
import {AbsenceVerificationConfirmationByTeacher} from '@model/absences-verification';
import {Class} from '@model/class';
import {ClassStatistic} from '@model/class-statistic';
import {EncodedAbsence} from '@model/encoded-absence';
import {OptionChoiceOverview} from '@model/option-choice-overview';
import {Page} from '@model/page';
import {Profile} from '@model/profile';
import {Schedule} from '@model/schedule';
import {School} from '@model/school';
import {StudentSearchData} from '@model/student-search-data';
import {StudentView} from '@model/student-view';
import {select, Store} from '@ngrx/store';
import {ClassStatisticLoadedAction, SchoolSectionOptionOverviewLoadedAction} from '@redux/actions/school.action';
import {absencesVerificationsConfirmationsLoaded} from '@redux/actions/student-absence.action';
import {StudentStaffMemberEncodedStudentAbsenceLoaded} from '@redux/actions/students.action';
import {getAbsenceVerificationConfirmationByTeacher, getClassesStatistic, getSectionOptionOverview, getStudentEncodedAbsences, StoreState} from '@redux/reducers';
import {SchoolService} from '@services/school.service';
import {StudentService} from '@services/student.service';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {DateTime} from 'luxon';
import {combineLatest, Observable} from 'rxjs';
import {switchMap, take} from 'rxjs/operators';

@Injectable()
export class StatisticsFacadeService {
  school$: Observable<School>;
  sectionOptionOverview$: Observable<OptionChoiceOverview>;
  staffMemberEncodedStudentAbsences$: Observable<EncodedAbsence[]>;
  registrationYear$: Observable<number>;
  activeSchoolYear$: Observable<number>;
  classesStatistic$: Observable<ClassStatistic[]>;
  isMobile$: Observable<boolean>;
  classes$: Observable<Class[]>;
  profile$: Observable<Profile>;
  schedule$: Observable<Schedule>;
  absencesVerificationConfirmations$: Observable<AbsenceVerificationConfirmationByTeacher[]>;
  studentSearchResult$: Observable<Page<StudentView>>;

  constructor(private _store: Store<StoreState>,
    private _sharedFacade: SharedFacadeService,
    private _schoolService: SchoolService,
    private _studentService: StudentService,
    ) {
    this.school$ = this._sharedFacade.school$;
    this.sectionOptionOverview$ = this._store.pipe(select(getSectionOptionOverview));
    this.registrationYear$ = this._sharedFacade.registrationYear$;
    this.activeSchoolYear$ = this._store.pipe(
      select(state => state.school.school.activeSchoolYear)
    );
    this.staffMemberEncodedStudentAbsences$ = this._store.pipe(select(getStudentEncodedAbsences));
    this.classesStatistic$ = this._store.pipe(select(getClassesStatistic));
    this.isMobile$ = this._sharedFacade.isMobile$;
    this.classes$ = this._sharedFacade.classes$;
    this.profile$ = this._sharedFacade.profile$;
    this.schedule$ = this._sharedFacade.schedule$;
    this.absencesVerificationConfirmations$ = this._store.pipe(select(getAbsenceVerificationConfirmationByTeacher));
    this.studentSearchResult$ = this._sharedFacade.studentSearchResult$;
  }

  loadSectionOptionOverview() {
    combineLatest([this.school$, this.registrationYear$]).pipe(
      take(1),
      switchMap(([school, attributionYear]) => this._schoolService.getOptionChoiceOverview(school.id, attributionYear))
    ).subscribe(sectionOptionOverview => this._store.dispatch(new SchoolSectionOptionOverviewLoadedAction(sectionOptionOverview)));
  }

  updateRegistrationYear(number: any) {
    this._sharedFacade.updateRegistrationYear(number);
  }

  loadRegistrationYear() {
    this._sharedFacade.loadRegistrationYear();
  }

  loadStaffMemberEncodedStudentAbsences(from: DateTime, to: DateTime) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadStaffMemberEncodedStudentAbsenceStatistic(school.id, from, to))
    ).subscribe(encodedStudentAbsences => this._store.dispatch(new StudentStaffMemberEncodedStudentAbsenceLoaded(encodedStudentAbsences)));
  }

  loadClassStatistic() {
    combineLatest([this.school$, this.registrationYear$]).pipe(
      take(1),
      switchMap(([school, attributionYear]) => this._schoolService.getClassesStatistic(school.id, attributionYear))
    ).subscribe(classesStatistic => this._store.dispatch(new ClassStatisticLoadedAction(classesStatistic)));
  }

  loadSchedule() {
    this._sharedFacade.loadSchedule();
  }
  
  loadAbsencesVerification(date: DateTime, hour: string) {
    this.school$.pipe(
      take(1),
      switchMap((school) => this._studentService.loadAbsencesVerificationsConfirmations(school.id, date, hour))
    ).subscribe(absenceVerificationConfirmation => {
      this._store.dispatch(absencesVerificationsConfirmationsLoaded({ absenceVerificationConfirmation }));

    });
  }

  searchStudents(searchData: StudentSearchData): void {
    this._sharedFacade.searchStudents(searchData);
  }

  resetStudentSearchResult() {
    this._sharedFacade.resetStudentSearchResult();
  }
}
